import { useState } from 'react';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import type { PaymentReconComment } from '../../types/paymentRecon';

interface Props {
    invoiceId: number;
    comments: PaymentReconComment[];
    canEdit: boolean;
    onChanged: () => void;
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('de-CH', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

/**
 * Free-text comment thread for one invoice (add / edit / delete). Adapted from
 * the Credit Limits comments widget, without the type selector.
 */
export function InvoiceComments({ invoiceId, comments, canEdit, onChanged }: Props) {
    const [newText, setNewText] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAdd = async () => {
        if (!newText.trim()) return;
        setLoading(true);
        try {
            await paymentReconApi.createInvoiceComment(invoiceId, newText.trim());
            setNewText('');
            onChanged();
        } catch (err) {
            console.error('Failed to create comment:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (commentId: number) => {
        if (!editText.trim()) return;
        setLoading(true);
        try {
            await paymentReconApi.updateInvoiceComment(invoiceId, commentId, editText.trim());
            setEditingId(null);
            onChanged();
        } catch (err) {
            console.error('Failed to update comment:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (commentId: number) => {
        setLoading(true);
        try {
            await paymentReconApi.deleteInvoiceComment(invoiceId, commentId);
            onChanged();
        } catch (err) {
            console.error('Failed to delete comment:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {/* Comment list */}
            <div className="mb-3 space-y-2">
                {comments.map(c => (
                    <div key={c.Id} className="rounded border border-slate-200 p-2 text-sm">
                        <div className="mb-1 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">{c.CreatedBy}</span>
                                <span className="text-xs text-slate-400">{formatDate(c.CreatedAt)}</span>
                                {c.UpdatedAt && <span className="text-xs italic text-slate-400">(edited)</span>}
                            </div>
                            {canEdit && editingId !== c.Id && (
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => { setEditingId(c.Id); setEditText(c.CommentText); }}
                                        className="p-1 text-slate-400 hover:text-slate-600"
                                    >
                                        <Pencil className="h-3 w-3" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(c.Id)}
                                        disabled={loading}
                                        className="p-1 text-slate-400 hover:text-rose-600"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                        {editingId === c.Id ? (
                            <div className="mt-1 flex gap-2">
                                <input
                                    type="text"
                                    value={editText}
                                    onChange={e => setEditText(e.target.value)}
                                    className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
                                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(c.Id); if (e.key === 'Escape') setEditingId(null); }}
                                    autoFocus
                                />
                                <button onClick={() => handleUpdate(c.Id)} disabled={loading} className="p-1 text-emerald-600 hover:text-emerald-700">
                                    <Check className="h-4 w-4" />
                                </button>
                                <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:text-slate-600">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <p className="whitespace-pre-wrap text-slate-700">{c.CommentText}</p>
                        )}
                    </div>
                ))}
                {comments.length === 0 && (
                    <p className="text-xs text-slate-400">No comments yet.</p>
                )}
            </div>

            {/* Add new comment */}
            {canEdit && (
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newText}
                        onChange={e => setNewText(e.target.value)}
                        placeholder="Add comment…"
                        className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
                        onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                    />
                    <button
                        onClick={handleAdd}
                        disabled={loading || !newText.trim()}
                        className="rounded bg-amber-600 px-3 py-1 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>
            )}
        </div>
    );
}
